import * as moment from 'moment';

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
  GetCftDocuments,
  GetGroups,
  GetIndividualCftScores,
  GetIndividualPftScores,
  GetPftDocuments,
  GetPlansPublishedToAllGroups,
  SelectGroup,
  SelectMarine,
  SetTabState,
} from 'app/explore/state/explore.actions';
import { Observable, Subscription } from 'rxjs';

import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { User } from 'app/classes/user';
import { PftCftObservationDocument } from 'app/explore/models/individual-pft-cft-score.model';
import { TabState } from 'app/explore/models/tab-state.model';
import { ExploreSelectors } from 'app/explore/state/explore.selectors';
import { Group } from 'app/group-management/models/group.model';
import { AuthService } from 'app/services/auth.service';
import { OfflineService } from 'app/services/offline.service';
import { PlanMetadata } from 'fitforce-document-sync';

@Component({
  selector: 'pft-cft-scores',
  templateUrl: './pft-cft-scores.component.html',
  styleUrls: ['./pft-cft-scores.component.css'],
})
export class PftCftScoresComponent implements OnInit, OnDestroy {
  private subs: Subscription[] = [];

  readonly marineColumns = ['date', 'workoutName', 'groupName', 'isComplete', 'actualTrainingLoad', 'estTrainingLoad'];
  readonly groupColumns = ['date', 'planName', 'playcardName', 'actualTrainingLoad', 'estTrainingLoad'];
  readonly defaultNumWeeks = 6;
  readonly scoreCategories = ['PFT', 'CFT'];

  loading = false;
  loadingPftScores = false;
  loadingCftScores = false;
  marines: User[] = [];
  selectedGroupPlans: PlanMetadata[] = [];
  groups: Group[] = [];
  selectedMarine: User;
  selectedMarineId: string;
  selectedGroup: Group;
  offlineStatusSubscription: Subscription;
  tabState: TabState = 0;
  search: FormControl;
  pageNumber = 0;

  pftLineSeries: number[] = [];
  cftLineSeries: number[] = [];
  pftSeriesLabels: string[] = [];
  cftSeriesLabels: string[] = [];
  lineLabel = 'Actual Score';
  thresholdValues = [235, 200, 150];
  thresholdLabels = ['1st Class', '2nd Class', '3rd class'];
  thresholdColors = ['#02b30b', '#f7bf05', '#c40212']; // green, yellow, red

  // TODO: Update <any> with model for pftCftScores
  dataSourceIndividuals = new MatTableDataSource<any>();
  dataSourceGroups = new MatTableDataSource<any>();

  scores = {
    pft: [],
    cft: [],
  };

  @Select(ExploreSelectors.loading) loading$: Observable<boolean>;
  @Select(ExploreSelectors.loadingPftScores) loadingPftScores$: Observable<boolean>;
  @Select(ExploreSelectors.loadingCftScores) loadingCftScores$: Observable<boolean>;
  @Select(ExploreSelectors.marines) marines$: Observable<User[]>;
  @Select(ExploreSelectors.groups) groups$: Observable<Group[]>;
  @Select(ExploreSelectors.selectedMarine) selectedMarine$: Observable<User>;
  @Select(ExploreSelectors.selectedGroup) selectedGroup$: Observable<Group>;
  @Select(ExploreSelectors.tabState) tabState$: Observable<TabState>;
  @ViewChild('paginator', { static: true }) paginator: MatPaginator;

  @Select(ExploreSelectors.individualPftScoreDocuments) individualPftScoreDocuments$: Observable<PftCftObservationDocument[]>;
  @Select(ExploreSelectors.groupPftScoreDocuments) groupPftScoreDocuments$: Observable<PftCftObservationDocument[]>;
  @Select(ExploreSelectors.individualCftScoreDocuments) individualCftScoreDocuments$: Observable<PftCftObservationDocument[]>;
  @Select(ExploreSelectors.groupCftScoreDocuments) groupCftScoreDocuments$: Observable<PftCftObservationDocument[]>;

  currentUser$: Observable<User>;
  currentUser: User;

  get isFormValid() {
    return this.selectedGroup;
  }

  constructor(
    private store: Store,
    private offline: OfflineService,
    private router: Router,
    private snackBar: MatSnackBar,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.search = new FormControl(undefined);
    this.search.patchValue(undefined);
    this.currentUser$ = this.auth.currentUserObs;

    this.subs.push(
      this.currentUser$.subscribe((user: User) => {
        this.currentUser = user;
      }),
      this.loading$.subscribe(value => {
        this.loading = value;
      }),
      this.loadingPftScores$.subscribe(value => {
        this.loadingPftScores = value;
      }),
      this.loadingCftScores$.subscribe(value => {
        this.loadingCftScores = value;
      }),
      this.marines$.subscribe(value => {
        this.marines = value;
      }),
      this.groups$.subscribe(value => {
        if (value) {
          this.groups = value;
          this.groups = this.groups.slice().sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));

          const groupUUIDs = this.groups.map(group => group.uuid);
          this.store.dispatch(new GetPlansPublishedToAllGroups(groupUUIDs));
        }
      }),
      this.selectedMarine$.subscribe(value => {
        this.selectedMarine = value;
        this.clearChartData();
        if (value) {
          this.store.dispatch(new GetIndividualPftScores(value));
          this.store.dispatch(new GetIndividualCftScores(value));
        }
      }),
      this.selectedGroup$.subscribe(value => {
        this.selectedGroup = value;
        this.clearChartData();
        if (value) {
          this.store.dispatch(new GetPftDocuments(value.uuid));
          this.store.dispatch(new GetCftDocuments(value.uuid));
        }
      }),
      this.tabState$.subscribe(value => {
        this.tabState = value;
      }),
      this.individualPftScoreDocuments$.subscribe(value => {
        this.scores.pft = value;
        this.buildChartData(this.scores.pft, 'PFT');
      }),
      this.groupPftScoreDocuments$.subscribe(value => {
        this.scores.pft = value;
        this.buildChartData(this.scores.pft, 'PFT');
      }),
      this.individualCftScoreDocuments$.subscribe(value => {
        this.scores.cft = value;
        this.buildChartData(this.scores.cft, 'CFT');
      }),
      this.groupCftScoreDocuments$.subscribe(value => {
        this.scores.cft = value;
        this.buildChartData(this.scores.cft, 'CFT');
      }),
    );
    this.offlineStatusSubscription = this.offline.status.subscribe(value => {
      const isOnline = value;
      if (!isOnline) {
        this.snackBar.open('Explore is not available in offline mode.', 'Dismiss', { duration: 5000 });
        this.router.navigate(['dashboard']);
      }
    });

    this.store.dispatch(GetGroups);
  }

  ngOnDestroy() {
    this.offlineStatusSubscription.unsubscribe();
    this.subs.forEach(sub => sub.unsubscribe());
  }

  selectMarine(marine: User) {
    this.store.dispatch(new SelectMarine(marine.uuid));
    this.store.dispatch(new SetTabState(TabState.MARINES));
  }

  selectGroup(group: Group) {
    this.store.dispatch(new SelectGroup(group));
    this.store.dispatch(new SetTabState(TabState.GROUPS));
  }

  buildChartData(value: any, type: string) {
    if (value && value.length > 0) {
      // sort results for PFT (remove this once data is returned sorted from backend)
      const analyses = value
        .map(val => ({ score: val.document.result.score, timestamp: moment(val.document.timestamp).hours(0).minutes(0).seconds(0) }))
        .sort((a, b) => a.timestamp - b.timestamp);
      // average scores from same day
      const averages = this.averageAnalyses(analyses);
      if (averages.length === 1) {
        averages.push({ score: undefined, timestamp: moment(Date.now()).hours(24).minutes(0).seconds(0) });
      }
      if (type === 'PFT') {
        this.pftLineSeries = [];
        this.pftSeriesLabels = [];
        averages.forEach(pft => {
          this.pftLineSeries.push(pft.score);
          this.pftSeriesLabels.push(pft.timestamp.format('M/D/YY'));
        });
      } else if (type === 'CFT') {
        this.cftLineSeries = [];
        this.cftSeriesLabels = [];
        averages.forEach(cft => {
          this.cftLineSeries.push(cft.score);
          this.cftSeriesLabels.push(cft.timestamp.format('M/D/YY'));
        });
      }
    } else {
      this.clearChartData();
    }
  }

  clearChartData() {
    this.dataSourceIndividuals.data = [];
    this.dataSourceGroups.data = [];
    this.pftLineSeries = [];
    this.cftLineSeries = [];
    this.pftSeriesLabels = [];
    this.cftSeriesLabels = [];
  }

  averageAnalyses(data: any) {
    const scoreBuckets = {};

    // group data points by timestamp
    data.forEach((d: any) => {
      if (scoreBuckets[d.timestamp] === undefined) {
        scoreBuckets[d.timestamp] = [];
      }
      scoreBuckets[d.timestamp].push(d);
    });

    // return single object with the average score on each date
    const ret = [];
    Object.keys(scoreBuckets).forEach(key => {
      const score = scoreBuckets[key].reduce((a, b) => a + b.score, 0) / scoreBuckets[key].length;
      const timestamp = scoreBuckets[key][0].timestamp;
      ret.push({ score: score, timestamp: timestamp });
    });

    return ret;
  }
}
